import React from "react";
import { alpha, Box, Stack, Typography } from "@mui/material";
import { parseDateToLgComun } from "../../../helpers/dateTransform";

/**
 * Piezas compartidas por las dos listas de resultados.
 *
 * Denuncias e investigaciones son el mismo objeto visual —un resultado de
 * búsqueda— así que el estado, la línea de metadatos, la fecha y el bloque de
 * la fila se definen una sola vez.
 *
 * El criterio del conjunto es que una lista de resultados se lee de corrido, no
 * se inspecciona tarjeta por tarjeta: el único trazo que queda es el divisor
 * entre resultados. Nada de recuadros, píldoras ni barras laterales; el color
 * entra por el texto y por un punto, que es suficiente para distinguir un
 * estado de otro sin dibujarle una caja alrededor.
 */

/**
 * "1 Oct 2026". El separador por defecto del helper (" - ") mete tres tokens
 * donde basta uno y compite con los "·" que separan los metadatos.
 */
export const formatResultDate = (date) =>
  date ? parseDateToLgComun(date, " ") : "";

/**
 * Estado: un punto de color y la palabra, sin recuadro.
 *
 * Antes era una píldora con borde de 1.5px y fondo teñido. Una por resultado
 * pasa, pero las denuncias ligadas traen la suya y una tarjeta terminaba con
 * tres cajas de colores distintos peleándose la atención con el título.
 */
export const StatusTag = ({ label, color, fontSize = 11 }) => {
  if (!label) return null;

  return (
    <Stack
      component="span"
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{ display: "inline-flex", flexShrink: 0 }}
    >
      <Box
        component="span"
        aria-hidden
        sx={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          bgcolor: color || "text.disabled",
          flexShrink: 0,
        }}
      />
      <Typography
        variant="body2"
        component="span"
        fontSize={fontSize}
        fontWeight={600}
        sx={{ color: color || "text.secondary", whiteSpace: "nowrap" }}
      >
        {label}
      </Typography>
    </Stack>
  );
};

/**
 * Metadatos separados por "·". El punto lo pone el componente, no cada texto:
 * así nunca sobra ni falta uno cuando un dato viene vacío.
 */
export const MetaText = ({ items = [], fontSize = 12, sx }) => {
  const parts = items.filter(Boolean);

  if (parts.length === 0) return null;

  return (
    <Typography
      variant="body2"
      component="span"
      fontSize={fontSize}
      color="text.secondary"
      sx={{
        display: "inline-flex",
        flexWrap: "wrap",
        alignItems: "center",
        columnGap: 0.75,
        ...sx,
      }}
    >
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <Box component="span" aria-hidden sx={{ color: "text.disabled" }}>
              ·
            </Box>
          )}
          <Box component="span">{part}</Box>
        </React.Fragment>
      ))}
    </Typography>
  );
};

/** Identificador largo y poco legible (el alias de una investigación). */
export const KeyTag = ({ value, title, children, sx }) => {
  if (!value) return null;

  return (
    <Typography
      variant="body2"
      component="span"
      title={title ? `${title}: ${value}` : value}
      fontSize={11}
      sx={{
        ml: "auto",
        pl: 1,
        maxWidth: { xs: 110, sm: 180 },
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        color: "text.disabled",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
};

/** Etiqueta de un bloque secundario dentro del resultado. */
export const GroupLabel = ({ children }) => (
  <Typography
    variant="body2"
    component="span"
    fontSize={11}
    color="text.disabled"
  >
    {children}
  </Typography>
);

/** La fila completa de un resultado: aire, sin caja. */
export const resultRowSx = (theme) => ({
  py: 2.25,
  px: { xs: 1, md: 2 },
  borderRadius: 2,
  rowGap: 0.75,
  transition: "background-color 0.15s ease-in-out",
  "&:hover": { bgcolor: alpha(theme.palette.blueGray100.main, 0.35) },
  // Las acciones aparecen al pasar el cursor; en táctil siempre se ven
  "&:hover .search-result-actions, &:focus-within .search-result-actions": {
    opacity: 1,
  },
});

/** El único trazo de la lista, y lo más tenue que siga sirviendo de guía. */
export const resultDividerSx = (theme) => ({
  mx: { xs: 1, md: 2 },
  borderColor: alpha(theme.palette.blueGray200.main, 0.45),
});

/** Título del resultado: lo único que pesa en la tarjeta. */
export const resultTitleSx = {
  fontSize: 17,
  fontWeight: 600,
  lineHeight: 1.35,
  width: "fit-content",
  maxWidth: "100%",
};

/** Botonera de la esquina inferior derecha. */
export const resultActionsSx = {
  opacity: { xs: 1, md: 0 },
  transition: "opacity 0.15s ease-in-out",
};
